import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Frame} from "types/Frame";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon, UserIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteFrame from "pages/Frame/Delete";
import {formattedDate} from "services/date";
import {humanFileSize} from "services/string";
import StackedList from 'components/StackedList';
import StackedListItem from 'components/StackedListItem';
import {CameraIcon} from "@heroicons/react/outline";
import {useOperator} from "context/OperatorContext";
import IconBadge from "../../../components/IconBadge";
import IconName from "../../../types/IconName";

function ViewFrame() {
  const {id} = useParams()
  const {data: frame, isLoading} = useQuery(
    ['frame', {id}],
    () => ngage.frames.get(parseInt(id as string))
  )

  const [deletingFrame, setDeletingFrame] = useState<Frame | null>(null);
  const {notify} = useNotifications()
  const navigate = useNavigate()
  const {subdomain} = useOperator()

  // Prepare action handlers
  function handleDeleteFrame() {
    if (frame) setDeletingFrame(frame)
  }

  function handleEditFrame() {
    if (id) navigate(`/frames/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteFrame}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditFrame}>Edit</Button>
  </>

  return (
    <Page
      title={frame?.name || "View Frame"}
      breadcrumbs={{"Engagement": "/", "Frames": "/frames", [frame?.name || "View Frame"]: `/frames/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {frame ?
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Frame Information */}
          <section className="lg:col-span-2">
            <Card
              heading="Frame Information"
              subheading={`${frame.is_published ? 'Published' : 'Unpublished'} frame`}
            >
              <DescriptionList>
                <DescriptionListItem span={1}>
                  <DescriptionTerm>Uploaded</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(frame.created_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Published</DescriptionTerm>
                  <DescriptionDetails>
                    {frame.is_published ? formattedDate(frame.published_at) : 'No'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Image Type</DescriptionTerm>
                  <DescriptionDetails>
                    {frame.asset.mime_type}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Size</DescriptionTerm>
                  <DescriptionDetails>
                    {humanFileSize(frame.asset.size)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>File</DescriptionTerm>
                  <DescriptionDetails>
                    <StackedList>
                      <StackedListItem
                        icon={CameraIcon}
                        action={<a
                          href={ngage.frames.downloadUrl(frame.id, subdomain)}
                          className="font-medium text-brand hover:text-brand/80">
                          Download</a>
                        }>
                        Original Image
                      </StackedListItem>
                    </StackedList>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Sites</DescriptionTerm>
                  <DescriptionDetails>
                    {frame.sites.length < 1 &&
                      <span>All</span>
                    }

                    {frame.sites.map(site => (
                      <IconBadge
                        key={site.id}
                        color={IconBadge.colors.BRAND}
                        size={IconBadge.sizes.LARGE}
                        icon={IconName.OFFICE_BUILDING}
                        text={site.name}
                      />
                    ))}
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          <section className="mt-4 lg:mt-0 lg:col-span-1">
            <Card heading="Preview">
              <div className="relative flex justify-center items-center overflow-hidden">
                <img
                  src={frame.asset.original_url}
                  alt={`${frame.name} preview`}
                  className="z-10"
                />
                <UserIcon className="absolute top-16 lg:top-12 left-0 right-0 h-auto text-gray-200"/>
              </div>
            </Card>
          </section>
        </div>
        : null}

      {/* Delete confirmation modal */}
      <DeleteFrame
        show={frame === deletingFrame}
        frame={deletingFrame}
        onCancel={() => setDeletingFrame(null)}
        onDelete={frame => {
          navigate('/frames')
          notify("success", "Frame Deleted", `${frame.name} has been permanently removed`)
        }}
        onDeleteFail={frame => {
          setDeletingFrame(null)
          notify("error", "Frame Not Deleted", `${frame.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewFrame;